// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState, useEffect } from 'react'
import { isGatewayPayment } from '@/lib/paymentGateway'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { CreditCard, Download, Loader2, Printer, Search } from 'lucide-react'
import { formatBaht } from '@/lib/money'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Card, DataTable, Pagination, EmptyState, Button, Badge, Select, Input, Checkbox, Modal, Skeleton, CardKebabMenu, SetupGuide } from '@/components/ui'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import { PeriodAffordanceBanner } from '@/components/ui/PeriodAffordanceBanner'
import { useListSort } from '@/hooks/useListSort'
import { useSupportSession } from '@/hooks/useSupportSession'
import { useSelectedPropertyStore } from '@/stores'
import { useProperties } from '@/hooks/useProperties'
import { useAuth } from '@/hooks/useAuth'
import { ExportModal } from '@/components/export/ExportModal'
import {
  VerifyPaymentModal,
  RejectPaymentModal,
  RetryVerificationModal,
  FlagFraudModal,
} from '@/components/payments/VerificationActionModals'
import { IdentifyPayerModal } from '@/components/payments/IdentifyPayerModal'
import { fetchAllPayments } from '@/components/export/exportUtils'
import { usePayments } from '@/hooks/usePayments'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useDateFormatter, useLocalizationConfig } from '@/hooks/useDateFormatter'
import { useDocumentTemplates } from '@/hooks/useDocumentTemplates'
// WEB-H7: PDF generators are dynamic-imported at the call sites (pdfme weight).
import { fetchInvoice } from '@/hooks/useInvoices'
import { generateBillingPeriods, getMonthRange } from '@/lib/billingPeriod'
import type { PaymentListItem } from '@/hooks/usePayments'
import { useDeepLinkParams } from '@/hooks/useDeepLinkParams'
import type { Payment, Invoice } from '@/types'
import { ML_MATCH_CHIP } from '@/components/payments/mlMatchChip'
import type { Column } from '@/components/ui/DataTable'
import type { Template } from '@pdfme/common'

function buildPaymentAndInvoice(p: PaymentListItem): { payment: Payment; invoice: Invoice } {
  const payment: Payment = {
    id: p.id,
    invoice_id: p.invoice_id,
    amount: p.amount,
    payment_method: p.payment_method as Payment['payment_method'],
    paid_at: p.paid_at,
    reference: p.reference,
    slip_url: p.slip_url,
    created_at: p.created_at,
    updated_at: p.created_at,
  }
  const invoice: Invoice = {
    id: p.invoice_id,
    number: p.invoice_number || `#${p.invoice_id}`,
    customer: p.customer ? { id: p.customer.id, name: p.customer.name, phone: p.customer.phone } : undefined,
    unit: p.unit ? { id: p.unit.id, name: p.unit.name } : undefined,
    property: p.property ? {
      id: p.property.id,
      name: p.property.name,
      address: p.property.address,
      tax_id: p.property.tax_id,
      phone: p.property.phone,
      email: p.property.email,
      payment_methods: p.property.payment_methods,
    } : undefined,
    items: p.invoice_items?.map(item => ({
      id: item.id,
      invoice_id: p.invoice_id,
      item_type: item.item_type,
      description: item.description,
      quantity: item.quantity,
      unit_price: item.unit_price,
      amount: item.amount,
    })),
    total: p.invoice_total ?? 0,
    due_at: p.invoice_due_at || '',
    billed_at: p.invoice_billed_at || '',
  } as Invoice
  return { payment, invoice }
}

export function PaymentListPage() {
  const { t, i18n } = useTranslation('payments')
  const { t: tc } = useTranslation('common')
  const supportSession = useSupportSession().active
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'
  // EP-143 T143-013: Flag as fraud is staff+ (staff/admin/owner, not accountant).
  const isStaffOrHigher = user?.role === 'staff' || user?.role === 'admin' || user?.role === 'owner'
  const isStaff = user?.role === 'staff'
  // T149-074 (MON-19 C): identify_payer? is admin_or_higher? || accountant?,
  // the same gate as update?/verify?/reject?; staff never sees the action.
  const canIdentifyPayer = isAdminOrOwner || user?.role === 'accountant'
  const formatDate = useDateFormatter()
  const { calendar } = useLocalizationConfig()

  const [page, setPage] = useState(1)
  const [method, setMethod] = useState<string>('')
  // EP-161 §2.4: '' or 'parking_kiosk' (kiosk parking payments only).
  const [channel, setChannel] = useState<string>('')
  // EP-155: the dashboard's "slips to review" row links here with
  // ?verification_status=pending&all_periods=true; a slip can sit in an
  // earlier period, so the link also opts out of the latest-period default.
  const deepLink = useDeepLinkParams(['verification_status', 'all_periods'] as const)
  const [verificationStatus, setVerificationStatus] = useState<string>(deepLink.verification_status ?? '')
  const [billingPeriod, setBillingPeriod] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  // T149-074: round-trips through the URL so the closing package checklist
  // (and any other deep link) can land here pre-filtered with
  // /payments?unidentified=true. Read straight from the URL, with no copy
  // in state: a copy kept the filter on after the sidebar link or the back
  // button had already moved the URL to plain /payments, which left the
  // full list showing as empty.
  const [searchParams, setSearchParams] = useSearchParams()
  const unidentifiedOnly = searchParams.get('unidentified') === 'true'
  // EP-97: Payments is a RENT-ROLL page — defaults to the latest payment period with
  // rows. `allPeriods` sends all_periods=true to escape that default (set when the
  // user explicitly picks "All periods" or clicks the affordance banner).
  const [allPeriods, setAllPeriods] = useState(deepLink.all_periods === 'true')
  const [showExportModal, setShowExportModal] = useState(false)
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [downloadingReceiptId, setDownloadingReceiptId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  // Manual slip verification from the list — EP-143: parking ML auto-verifies
  // when all gates pass; this is the operator action for fraud-suspect / review
  // parking payments and for rental payments where RDCW decides.
  const [verificationAction, setVerificationAction] = useState<
    { type: 'verify' | 'reject' | 'retry' | 'flag_fraud'; paymentId: number } | null
  >(null)
  // T149-074: the ระบุผู้จ่าย modal, shared with PaymentDetailPage's own copy
  // of this same component, keyed by the row it was opened from.
  const [identifyPayerPaymentId, setIdentifyPayerPaymentId] = useState<number | null>(null)

  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)
  const { data: propertiesData } = useProperties(1)
  const properties = propertiesData?.data ?? []

  const handleUnidentifiedToggle = (checked: boolean) => {
    setPage(1)
    const next = new URLSearchParams(searchParams)
    if (checked) next.set('unidentified', 'true')
    else next.delete('unidentified')
    setSearchParams(next, { replace: true })
  }

  const activeFilterCount = [billingPeriod, method, channel, verificationStatus, from, to].filter(Boolean).length +
    (unidentifiedOnly ? 1 : 0)
  // True-empty: no payments and no filter or search is active.
  // Filtered: at least one filter/search is active but returned no results.
  const isFiltered = !!(searchQuery || method || channel || verificationStatus || billingPeriod || from || to || unidentifiedOnly)

  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'paid_at',
    defaultOrder: 'desc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  const { data: accountSettings } = useAccountSettings()
  const { data: receiptTemplates } = useDocumentTemplates(1, 'receipt')
  const { data, isLoading, isFetching, error } = usePayments(
    page,
    method || undefined,
    from || undefined,
    to || undefined,
    selectedPropertyId ?? undefined,
    searchQuery || undefined,
    verificationStatus || undefined,
    { ...sortParams, allPeriods, unidentified: unidentifiedOnly || undefined, channel: channel || undefined },
  )

  const payments = data?.data || []
  const meta = data?.meta
  // EP-97: format the resolved default period for the affordance banner (renders
  // nothing when source === 'explicit', i.e. the user chose a period range).
  const defaultPeriod = data?.meta?.default_period

  const sortOptions = [
    { value: 'unit_number', label: tc('sort.by_unit') },
    { value: 'amount', label: t('labels.amount') },
    { value: 'paid_at', label: t('labels.date') },
    { value: 'invoice_number', label: t('labels.invoice') },
    { value: 'verification_status', label: t('labels.status') },
  ]

  // Reset page on filter changes
  useEffect(() => {
    setPage(1)
  }, [method, channel, verificationStatus, billingPeriod, from, to, unidentifiedOnly])

  const billingPeriodOptions = [
    { value: '', label: t('filters.all_periods') },
    ...generateBillingPeriods({ locale: i18n.language, calendar }),
    { value: 'custom', label: t('filters.custom_range') },
  ]

  const handleBillingPeriodChange = (value: string) => {
    setBillingPeriod(value)
    if (value && value !== 'custom') {
      const { from: f, to: t } = getMonthRange(value)
      setFrom(f)
      setTo(t)
      setAllPeriods(false)
    } else if (value === '') {
      // Explicitly choosing "All periods" opts out of the latest-period default.
      setFrom('')
      setTo('')
      setAllPeriods(true)
    } else {
      // 'custom' — user will pick an explicit range below.
      setAllPeriods(false)
    }
    setPage(1)
  }

  // EP-97: affordance escape — jump from the default latest-period view to all periods.
  const handleShowAllPeriods = () => {
    setBillingPeriod('')
    setFrom('')
    setTo('')
    setAllPeriods(true)
    setPage(1)
  }

  // EP-97 fix (2026-08-19): rendered in BOTH the empty and has-rows branches.
  // It used to live only in the has-rows branch, so an account whose payments
  // all fall outside the current billing cycle saw an empty view with no sign
  // that a period filter was applied and no way to escape it.
  const periodBanner = defaultPeriod ? (
    <PeriodAffordanceBanner
      period={defaultPeriod}
      formattedPeriod={defaultPeriod.from ? formatDate(defaultPeriod.from) : ''}
      onShowAllPeriods={handleShowAllPeriods}
    />
  ) : null

  const handleDownloadReceipt = async (p: PaymentListItem) => {
    const tmpl = receiptTemplates?.data?.[0]?.template as Template | undefined
    if (!tmpl) return
    setDownloadingReceiptId(p.id)
    try {
      // Fetch full invoice (with items) for PDF generation
      const fullInvoice = await fetchInvoice(p.invoice_id)
      const payment: Payment = {
        id: p.id, invoice_id: p.invoice_id, amount: p.amount,
        payment_method: p.payment_method as Payment['payment_method'], paid_at: p.paid_at,
        reference: p.reference, slip_url: p.slip_url,
        created_at: p.created_at, updated_at: p.created_at,
      }
      const { downloadReceiptPdf } = await import('@/lib/receiptPdfGenerator')
      await downloadReceiptPdf(payment, fullInvoice, tmpl, accountSettings)
    } finally {
      setDownloadingReceiptId(null)
    }
  }

  const handlePrint = async () => {
    const tmpl = receiptTemplates?.data?.[0]?.template as Template | undefined
    if (!tmpl) return
    setIsPrinting(true)
    try {
      // MON-18 C: the print PDF follows the header's selected building, same
      // as the on-screen list and the CSV export.
      const allPayments = await fetchAllPayments(
        from || undefined,
        to || undefined,
        true,
        selectedPropertyId ?? undefined
      )
      if (allPayments.length === 0) {
        setShowPrintModal(false)
        return
      }

      const pairs = allPayments.map((p) => buildPaymentAndInvoice(p))
      const { generateMultiReceiptPdf } = await import('@/lib/receiptPdfGenerator')
      const pdf = await generateMultiReceiptPdf(pairs, tmpl, accountSettings)
      const blob = new Blob([pdf.buffer as ArrayBuffer], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setShowPrintModal(false)
    } finally {
      setIsPrinting(false)
    }
  }

  const getMethodBadge = (m: string) => {
    const variants: Record<string, 'success' | 'info' | 'warning' | 'ghost'> = {
      cash: 'success',
      transfer: 'info',
      promptpay: 'warning',
      credit_card: 'ghost',
    }
    return <Badge variant={variants[m] || 'ghost'}>{t(`methods.${m}`)}</Badge>
  }

  const getVerificationBadge = (status: string | undefined) => {
    if (!status) return null
    const variants: Record<string, 'warning' | 'info' | 'success' | 'error'> = {
      pending: 'warning',
      verifying: 'info',
      verified: 'success',
      rejected: 'error',
      failed: 'error',
    }
    return (
      <Badge variant={variants[status] || 'ghost'} size="sm">
        {t(`verification.statuses.${status}`)}
      </Badge>
    )
  }

  const columns: Column<PaymentListItem>[] = [
    {
      key: 'actions',
      header: '',
      className: 'w-10',
      render: (p) => {
        // Expose Verify/Reject/Retry on slip-verifiable payments. EP-143:
        // parking auto-verifies via ML when all gates pass; Verify/Reject are
        // needed for fraud-suspect/review routing and all rental payments.
        // Online (gateway) payments are settled by Stripe.
        const canModerate = isAdminOrOwner && !!p.verification_status && !isGatewayPayment(p)
        // EP-161 D4: staff verify and reject a kiosk parking payment too
        // (PaymentPolicy#verify?/#reject?); Retry stays admin and owner.
        const canDecide = canModerate || (isStaff && !!p.parking && !!p.verification_status)
        // EP-143 T143-013: Flag as fraud is staff+ AND the payment must not
        // already be verified (an already-settled payment can still be flagged
        // post-hoc via the detail page, but the list action is restricted to
        // un-verified payments where the flag action is most time-sensitive).
        const canFlagFraud = isStaffOrHigher && p.verification_status !== 'verified' && !isGatewayPayment(p)
        // T149-074: hidden once the payment has a customer (PaymentBlueprint's
        // customer field, not just p.customer_id, which the list payload
        // never carries), so the action only ever applies to a genuinely
        // unidentified payment (Payment.unidentified: no customer AND no
        // invoice; parking payments never surface it either).
        const canIdentifyThisPayment = canIdentifyPayer && !p.customer
        return (
          <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1">
            <CardKebabMenu
              ariaLabel={tc('labels.actions_menu')}
              items={[
                { label: tc('actions.view'), onClick: () => navigate(`/payments/${p.id}`) },
                { label: t('actions.download_receipt'), onClick: () => handleDownloadReceipt(p) },
                ...(canIdentifyThisPayment
                  ? [{ label: t('identify_payer.action'), onClick: () => setIdentifyPayerPaymentId(p.id) }]
                  : []),
                ...(canDecide && p.verification_status !== 'verified'
                  ? [{ label: t('actions.verify'), onClick: () => setVerificationAction({ type: 'verify', paymentId: p.id }) }]
                  : []),
                ...(canDecide && p.verification_status !== 'rejected'
                  ? [{ label: t('actions.reject'), variant: 'error' as const, onClick: () => setVerificationAction({ type: 'reject', paymentId: p.id }) }]
                  : []),
                ...(canModerate && p.has_slip
                  ? [{ label: t('actions.retry_verification'), onClick: () => setVerificationAction({ type: 'retry', paymentId: p.id }) }]
                  : []),
                ...(canFlagFraud
                  ? [{ label: t('actions.flag_fraud'), variant: 'error' as const, onClick: () => setVerificationAction({ type: 'flag_fraud', paymentId: p.id }) }]
                  : []),
              ]}
            />
            {downloadingReceiptId === p.id && <Loader2 className="w-3 h-3 animate-spin text-base-content/40" />}
          </div>
        )
      },
    },
    {
      key: 'amount',
      header: t('labels.amount'),
      sortable: true,
      render: (p) => (
        <span className="font-medium text-success whitespace-nowrap">
          {formatBaht(p.amount, { decimals: 2 })}
        </span>
      ),
    },
    {
      key: 'customer',
      header: t('labels.customer'),
      render: (p) => (
        <>
          {p.customer ? (
            <Link to={`/customers/${p.customer.id}`} className="link link-primary">
              {p.customer.name}
            </Link>
          ) : '-'}
          {/* EP-161 §2.4: on a phone the plate column is hidden, so a
              parking row carries its plate and zone on a line here. */}
          {p.parking && (
            <div className="md:hidden text-xs text-base-content/60" data-testid={`payment-parking-line-${p.id}`}>
              {parkingLine(p.parking)}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'plate',
      header: t('columns.plate'),
      hideBelow: 'md',
      render: (p) => p.parking ? (
        <span className="whitespace-nowrap">{parkingLine(p.parking)}</span>
      ) : '-',
    },
    {
      key: 'unit_number',
      header: t('labels.unit'),
      sortable: true,
      render: (p) => p.unit?.name || '-',
    },
    {
      key: 'paid_at',
      header: t('labels.date'),
      sortable: true,
      render: (p) => formatDate(p.paid_at),
    },
    {
      key: 'invoice_number',
      header: t('labels.invoice'),
      hideBelow: 'md',
      sortable: true,
      render: (p) => p.invoice_number ? (
        <Link to={`/invoices/${p.invoice_id}`} className="link link-primary">
          {p.invoice_number}
        </Link>
      ) : '-',
    },
    {
      key: 'payment_method',
      header: t('labels.method'),
      hideBelow: 'md',
      render: (p) => getMethodBadge(p.payment_method),
    },
    {
      key: 'verification_status',
      header: t('labels.status'),
      hideBelow: 'md',
      sortable: true,
      render: (p) => {
        // 1. Gateway payment — Stripe-branded badge. Keyed on the real gateway
        // signal (online + card/promptpay), never payment_type alone: legacy
        // online+transfer slip uploads must render their true slip status.
        if (isGatewayPayment(p)) {
          return (
            <Badge
              size="sm"
              className="gap-1 bg-[#635bff] text-white border-transparent"
            >
              <CreditCard className="w-3 h-3" />
              {t('verification.statuses.gateway')}
            </Badge>
          )
        }
        // 2. Slip-verified methods (bank transfer + parking QR) — real statuses.
        if (p.payment_method === 'transfer' || p.payment_method === 'qr_image') {
          if (!p.verification_status && !(p.has_slip ?? !!p.slip_url)) {
            return <Badge variant="error" size="sm">{t('verification.statuses.missing_slip')}</Badge>
          }
          // Record-only ML triage chip next to the authoritative status. Reasons
          // in the title so the operator can hover before opening the detail.
          // `reasons` is read without a default on purpose: the settlement
          // guarantees it and a spec pins it, so a client-side fallback would
          // only hide a broken writer (EP-145 A1).
          const mlChip = p.ml_match ? (
            <Badge
              variant={ML_MATCH_CHIP[p.ml_match.outcome].variant}
              size="sm"
              title={p.ml_match.reasons.map((r) => t(`verification.reasons.${r}`)).join('; ')}
            >
              {t(ML_MATCH_CHIP[p.ml_match.outcome].labelKey)}
            </Badge>
          ) : null
          return (
            <span className="inline-flex items-center gap-1">
              {getVerificationBadge(p.verification_status) ?? <span className="text-base-content/40">-</span>}
              {mlChip}
            </span>
          )
        }
        // 3. Manual (cash / promptpay / credit_card) — neutral ghost badge
        return (
          <Badge variant="ghost" size="sm">
            {t('verification.statuses.manual')}
          </Badge>
        )
      },
    },
    {
      key: 'reference',
      header: t('labels.reference'),
      hideBelow: 'lg',
      render: (p) => <span className="text-base-content/60">{p.reference || '-'}</span>,
    },
  ]

  const methodOptions = [
    { value: '', label: t('filters.all_methods') },
    { value: 'cash', label: t('methods.cash') },
    { value: 'transfer', label: t('methods.transfer') },
    { value: 'promptpay', label: t('methods.promptpay') },
    { value: 'credit_card', label: t('methods.credit_card') },
    { value: 'qr_image', label: t('methods.qr_image') },
  ]

  const channelOptions = [
    { value: '', label: t('filters.channel.all') },
    { value: 'parking_kiosk', label: t('filters.channel.parking_kiosk') },
  ]

  const verificationStatusOptions = [
    { value: '', label: t('filters.all_verification_statuses') },
    { value: 'pending', label: t('verification.statuses.pending') },
    { value: 'verifying', label: t('verification.statuses.verifying') },
    { value: 'verified', label: t('verification.statuses.verified') },
    { value: 'rejected', label: t('verification.statuses.rejected') },
    { value: 'failed', label: t('verification.statuses.failed') },
    // EP-143 T143-014: Fraud-suspect sub-filter — maps to
    // `fraud_suspect=true&verification_status=pending` at the API layer.
    { value: 'fraud_suspect', label: t('filters.fraud_suspect') },
  ]

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: t('title') }]} />

        <PageHeader
          title={t('title')}
          description={t('description')}
          isRefreshing={isFetching && !isLoading}
          titleAction={
            <Select
              data-testid="page-property-picker-mobile"
              aria-label={tc('labels.select_property')}
              value={selectedPropertyId ? String(selectedPropertyId) : ''}
              onChange={(e) => {
                setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)
                setPage(1)
              }}
              options={[
                { value: '', label: tc('labels.all_properties') },
                ...properties.map((p) => ({ value: String(p.id), label: p.name })),
              ]}
              className="max-w-[9rem]"
              size="sm"
            />
          }
          // Record Payment is the ONE create entry for every role (T149-088
          // merged the former admin-only invoice-attached form at
          // /payments/new into this same /payments/record; BE create? is
          // same_account? only, so there was never a real role restriction
          // on it, only a frontend-only gate this merge removes). Persona-sweep
          // lung-prasit: this page previously had no record-payment entry at all.
          primaryAction={{
            key: 'quick-pay',
            label: t('quick_pay.title'),
            icon: CreditCard,
            onClick: () => navigate('/payments/record'),
          }}
          overflowActions={[
            {
              key: 'print',
              label: t('actions.print_all'),
              icon: Printer,
              onClick: () => setShowPrintModal(true),
            },
            {
              key: 'export',
              label: tc('export_data.export_button'),
              icon: Download,
              onClick: () => setShowExportModal(true),
              // EP-160 S3: no CSV export in a FlowRent support session.
              hidden: supportSession,
            },
          ]}
        />

        {/* Filters */}
        <Card>
          {/* Search — always visible */}
          <div className="w-full min-w-0">
            <Input
              label={tc('actions.search')}
              leftIcon={<Search className="w-4 h-4" />}
              placeholder={t('filters.search_placeholder')}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1) }}
            />
          </div>

          {/* Mobile-only toggle for secondary filters */}
          <button
            type="button"
            className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium mt-3"
            aria-expanded={mobileFiltersOpen}
            aria-controls="payment-filters-body"
            onClick={() => setMobileFiltersOpen((v) => !v)}
          >
            <span>
              {tc('filters.toggle')}
              {activeFilterCount > 0 && (
                <Badge variant="count" size="sm" className="ml-2">{activeFilterCount}</Badge>
              )}
            </span>
            <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>

          <div
            id="payment-filters-body"
            className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
          >
            {/* EP-97: mobile-only sort control (desktop sorts via sortable table headers). */}
            <div className="w-full sm:hidden">
              <MobileSortControl
                options={sortOptions}
                value={sort}
                order={order}
                onChange={handleSort}
                onToggleOrder={handleToggleOrder}
              />
            </div>
            <Select
              label={t('filters.billing_period')}
              value={billingPeriod}
              onChange={(e) => handleBillingPeriodChange(e.target.value)}
              options={billingPeriodOptions}
              className="w-full sm:w-52 min-w-0"
            />
            <Select
              label={t('labels.method')}
              value={method}
              onChange={(e) => { setMethod(e.target.value); setPage(1) }}
              options={methodOptions}
              className="w-full sm:w-48 min-w-0"
            />
            <Select
              id="payment-filter-channel"
              label={t('filters.channel.label')}
              value={channel}
              onChange={(e) => { setChannel(e.target.value); setPage(1) }}
              options={channelOptions}
              className="w-full sm:w-40 min-w-0"
              data-testid="filter-channel"
            />
            <Select
              label={t('filters.verification_status')}
              value={verificationStatus}
              onChange={(e) => { setVerificationStatus(e.target.value); setPage(1) }}
              options={verificationStatusOptions}
              className="w-full sm:w-48 min-w-0"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full sm:flex-1 sm:min-w-[280px]">
              <Input
                label={t('filters.date_from')}
                type="date"
                value={from}
                onChange={(e) => { setFrom(e.target.value); setPage(1) }}
                disabled={billingPeriod !== '' && billingPeriod !== 'custom'}
              />
              <Input
                label={t('filters.date_to')}
                type="date"
                value={to}
                onChange={(e) => { setTo(e.target.value); setPage(1) }}
                disabled={billingPeriod !== '' && billingPeriod !== 'custom'}
              />
            </div>
            {/* T149-074 (MON-19 C): composes with search + verification status,
                same as the backend's apply_payment_filters. */}
            <div className="w-full">
              <Checkbox
                label={t('filters.unidentified')}
                checked={unidentifiedOnly}
                onChange={(e) => handleUnidentifiedToggle(e.target.checked)}
                data-testid="filter-unidentified"
              />
            </div>
          </div>
        </Card>

        {/* Table */}
        {isLoading ? (
          <Card>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          </Card>
        ) : error ? (
          <Card>
            <EmptyState
              title={tc('errors.something_went_wrong')}
              action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
            />
          </Card>
        ) : payments.length === 0 ? (
          // pb-[env(safe-area-inset-bottom)]: when the list is empty the Pagination
          // bar (and its spacer) never renders, so we manually clear the device
          // bottom chrome (home indicator / Android nav bar) on this card.
          <Card className="pb-[env(safe-area-inset-bottom)]">
            {periodBanner}
            {!isFiltered ? (
              // True-empty: no payments and no active filter/search.
              // Show the prerequisite chain and actionable next steps.
              <SetupGuide
                icon={<CreditCard className="w-16 h-16" />}
                title={t('setup_guide.title')}
                description={t('setup_guide.description')}
                steps={[
                  { title: t('setup_guide.step_1_title'), description: t('setup_guide.step_1_description') },
                  { title: t('setup_guide.step_2_title'), description: t('setup_guide.step_2_description') },
                  { title: t('setup_guide.step_3_title'), description: t('setup_guide.step_3_description') },
                ]}
                action={
                  <>
                    {/* Direct QuickPay escape from the invoice-first chain — a cash payment
                        does not require an invoice (EP-104 standalone payments). */}
                    <Button leftIcon={<CreditCard className="w-4 h-4" />} onClick={() => navigate('/payments/record')}>
                      {t('quick_pay.title')}
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/invoices')}>
                      {t('setup_guide.go_to_invoices')}
                    </Button>
                  </>
                }
              />
            ) : (
              // Filtered no-results: search/filter active but returned nothing.
              // Offer a single-tap escape to clear all filters and refetch.
              <EmptyState
                icon={<CreditCard className="w-12 h-12" />}
                title={t('empty.no_results')}
                description={t('empty.description')}
                action={
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('')
                      setMethod('')
                      setChannel('')
                      setVerificationStatus('')
                      setBillingPeriod('')
                      setFrom('')
                      setTo('')
                      handleUnidentifiedToggle(false)
                      // true, not false: the period window is applied SERVER-side
                      // when no from/to/all_periods is sent, so clearing the
                      // visible filters alone leaves it in force and the button
                      // looks like it does nothing.
                      setAllPeriods(true)
                      setPage(1)
                    }}
                  >
                    {t('empty.clear_filters')}
                  </Button>
                }
              />
            )}
          </Card>
        ) : (
          <Card>
            {periodBanner}
            <DataTable
              columns={columns}
              data={payments}
              keyExtractor={(p) => p.id}
              onRowClick={(p) => navigate(`/payments/${p.id}`)}
              sortBy={sort}
              sortOrder={order}
              onSort={handleSort}
            />
          </Card>
        )}

        {meta && meta.total_count > 0 && (
          <Pagination
            currentPage={meta.current_page}
            totalPages={meta.total_pages}
            totalItems={meta.total_count}
            pageSize={meta.per_page}
            onPageChange={setPage}
          />
        )}
      </div>

      {/* Print Modal */}
      <Modal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        title={t('actions.print_all')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowPrintModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button onClick={handlePrint} loading={isPrinting}>
              {isPrinting ? tc('actions.loading') : t('actions.print_all')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-base-content/60">
          {t('actions.print_all_description', { count: meta?.total_count ?? 0 })}
        </p>
      </Modal>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        dataType="payments"
        dateFrom={from || undefined}
        dateTo={to || undefined}
      />

      {/* Manual slip-verification actions (shared with PaymentDetailPage) */}
      {verificationAction && (
        <>
          <VerifyPaymentModal
            paymentId={verificationAction.paymentId}
            isOpen={verificationAction.type === 'verify'}
            onClose={() => setVerificationAction(null)}
          />
          <RejectPaymentModal
            paymentId={verificationAction.paymentId}
            isOpen={verificationAction.type === 'reject'}
            onClose={() => setVerificationAction(null)}
          />
          <RetryVerificationModal
            paymentId={verificationAction.paymentId}
            isOpen={verificationAction.type === 'retry'}
            onClose={() => setVerificationAction(null)}
          />
          <FlagFraudModal
            paymentId={verificationAction.paymentId}
            isOpen={verificationAction.type === 'flag_fraud'}
            onClose={() => setVerificationAction(null)}
          />
        </>
      )}

      {identifyPayerPaymentId !== null && (
        <IdentifyPayerModal
          paymentId={identifyPayerPaymentId}
          isOpen
          onClose={() => setIdentifyPayerPaymentId(null)}
        />
      )}
    </>
  )
}

// EP-161 §2.4: "plate · zone" of a kiosk parking payment.
function parkingLine(parking: NonNullable<PaymentListItem['parking']>): string {
  return [parking.license_plate, parking.zone_name].filter(Boolean).join(' · ')
}
